import React from 'react';
import { motion } from 'framer-motion';
import { QrCode, Tv, BarChart2, ChevronDown } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { DEMO_URL } from '../constants';

const HERO_BG = '/images/hero-stadium.png';

const stats = [
  { icon: QrCode, labelKey: 'stat1' },
  { icon: BarChart2, labelKey: 'stat2' },
  { icon: Tv, labelKey: 'stat3' },
];

export default function Hero({ onDemoClick }) {
  const { t } = useLanguage();
  const h = t.hero;

  return (
    <section id="hero" className="relative min-h-screen flex flex-col justify-center overflow-hidden pt-16"
      data-testid="hero-section">
      {/* Background */}
      <div className="absolute inset-0 z-0">
        <img src={HERO_BG} alt="Stadium" className="w-full h-full object-cover object-center" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#1F2833]/96 via-[#1F2833]/80 to-[#0F6B49]/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1F2833] via-transparent to-[#1F2833]/50" />
      </div>

      {/* Decorative */}
      <div className="absolute top-1/4 right-10 w-72 h-72 rounded-full bg-[#0F6B49]/10 blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/4 left-10 w-56 h-56 rounded-full bg-[#D5A63B]/8 blur-3xl pointer-events-none" />

      <div className="section-container relative z-10 py-24 md:py-32">
        <div className="max-w-3xl">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <span className="section-badge mb-6 inline-flex" data-testid="hero-badge">{h.badge}</span>
          </motion.div>

          <motion.h1 className="heading-xl mb-6" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }} data-testid="hero-headline">
            {h.h1_line1}<br />
            <span className="text-gradient-gold">{h.h1_highlight}</span>
          </motion.h1>

          <motion.p className="text-[#F3F1EB]/90 text-lg md:text-xl leading-relaxed mb-4 max-w-2xl font-medium"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}
            data-testid="hero-subtitle">
            {h.subtitle}
          </motion.p>

          <motion.p className="text-[#8A9099] text-base leading-relaxed mb-10 max-w-xl"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.3 }}>
            {h.body}
          </motion.p>

          <motion.div className="flex flex-wrap gap-4 mb-16"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.4 }}>
            <a href={DEMO_URL} target="_blank" rel="noopener noreferrer"
              className="btn-primary text-base px-8 py-3.5 no-underline" data-testid="hero-cta-primary">
              {h.cta1}
            </a>
            <a href="#como-funciona" className="btn-secondary text-base px-8 py-3.5" data-testid="hero-cta-secondary">
              {h.cta2}
            </a>

          </motion.div>

          {/* Stats */}
          <motion.div className="flex flex-wrap gap-6 md:gap-10"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.5 }}>
            {[h.stat1, h.stat2, h.stat3, h.stat4].map((label, i) => (
              <div key={i} className="flex items-center gap-2.5" data-testid={`hero-stat-${i}`}>
                <div className="w-2 h-2 rounded-full bg-[#D5A63B]" />
                <span className="text-[#F3F1EB] text-sm font-semibold">{label}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
        animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2 }}>
        <ChevronDown size={24} className="text-[#D5A63B]/60" />
      </motion.div>
    </section>
  );
}
